import { collapseAllNodeEvents, expandAllNodeEvents, resetEventFilters, setFocusedNodeId, setNodeEventAccordion, setShowChat, setShowEvents } from '@common-reducers/ChatbotReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { 
  EventType, 
  EventStatus, 
  NodeEvent, 
  CreditUsageEvent, 
  OutgoingMessageEvent, 
  IncomingMessageEvent, 
  SessionStartedEvent, 
  SessionEndedEvent, 
  ProcessType,
  ToolEvent,
  ProcessEvent
} from '@common/models/node-event';
import {
  ShowChart as ActivityIcon,
  Error as AlertCircleIcon,
  ArrowForward as ArrowRightIcon,
  UnfoldLess as CollapseAllIcon,
  UnfoldMore as ExpandAllIcon,
  HighlightOff as HighlightOffIcon,
  Message as MessageSquareIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  EventNote as EventIcon,
  Input as InputIcon,
  StopCircle as StopCircleIcon,
  CreditCard as CreditCardIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { Box, Button, ButtonGroup, CardContent, IconButton, Stack, Tooltip, Typography, Chip } from '@mui/material';
import { useEffect, useRef, useState, useMemo } from 'react';
import { MessageEvent } from './MessageEvent';
import { EventAccordion } from './EventAccordion';
import React from 'react';
import { selectUserObject } from '@/common/reducers/UserSelectors';

interface NodeEventsListProps {
  events: NodeEvent[];
}

const EVENT_TYPE_CONFIG: Record<EventType, { icon: React.ReactNode; label: string; color: string; bgColor: string }> = {
  [EventType.NODE_ENTRY]: {
    icon: <InputIcon />,
    label: 'Node Entry',
    color: '#2196f3',
    bgColor: '#e3f2fd'
  },
  [EventType.NODE_EXIT]: {
    icon: <ArrowRightIcon sx={{ transform: 'rotate(180deg)' }} />,
    color: '#27ae60',
    bgColor: 'rgba(39, 174, 96, 0.1)',
    label: 'Exit'
  },
  [EventType.PROCESS]: {
    icon: <ActivityIcon />,
    color: '#9b59b6',
    bgColor: 'rgba(155, 89, 182, 0.1)',
    label: 'Process'
  },
  [EventType.ERROR]: {
    icon: <AlertCircleIcon />,
    color: '#e74c3c',
    bgColor: 'rgba(231, 76, 60, 0.1)',
    label: 'Error'
  },
  [EventType.DECISION]: {
    icon: <MessageSquareIcon />,
    color: '#f1c40f',
    bgColor: 'rgba(241, 196, 15, 0.1)',
    label: 'Decision'
  },
  [EventType.OUTGOING_MESSAGE]: {
    icon: <SendIcon />,
    color: '#2ecc71',
    bgColor: 'rgba(46, 204, 113, 0.1)',
    label: 'Sent'
  },
  [EventType.INCOMING_MESSAGE]: {
    icon: <ChatIcon />,
    color: '#e67e22',
    bgColor: 'rgba(230, 126, 34, 0.1)',
    label: 'Received'
  },
  [EventType.SESSION_STARTED]: {
    icon: <EventIcon />,
    color: '#e67e22',
    bgColor: 'rgba(230, 126, 34, 0.1)',
    label: 'Session Started'
  },
  [EventType.SESSION_ENDED]: {
    icon: <StopCircleIcon />,
    label: 'Session Ended',
    color: '#f44336',
    bgColor: '#ffebee'
  },
  [EventType.CREDIT_USAGE]: {
    icon: <CreditCardIcon />,
    label: 'Credit Usage',
    color: '#9c27b0',
    bgColor: '#f3e5f5'
  },
  [EventType.TOOL_EVENT]: {
    icon: <BuildIcon />,
    label: 'Tool Usage',
    color: '#00796b',
    bgColor: '#e0f2f1'
  }
};

const NodeEventsList = ({ events }: NodeEventsListProps) => {
  const [accordionState, setAccordionState] = useState<Record<string, boolean>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const focusedNodeId = useAppSelector(state => state.ChatbotReducer.focusedNodeId);
  const showEvents = useAppSelector(state => state.ChatbotReducer.showEvents);
  const showChat = useAppSelector(state => state.ChatbotReducer.showChat);

  const user = useAppSelector(selectUserObject);
  const isAdmin = (user?.role === "support" || user?.role === "admin");

  // Calculate total credits used
  const totalCreditsUsed = useMemo(() => {
    return events.reduce((total, event) => {
      if (event.type === EventType.CREDIT_USAGE) {
        const creditData = event.data as CreditUsageEvent['data'];
        return total + (event.creditsUsed || creditData.amount || 0);
      }
      return total + (event.creditsUsed || 0);
    }, 0);
  }, [events]);

  // Calculate total tokens used
  const totalTokensUsed = useMemo(() => {
    return events.reduce((total, event) => {
      if (event.metadata?.tokenUsage?.totalTokens) {
        return total + event.metadata.tokenUsage.totalTokens;
      }
      return total;
    }, 0);
  }, [events]);

  // Calculate input and output tokens
  const { totalInputTokens, totalOutputTokens } = useMemo(() => {
    return events.reduce((acc, event) => {
      if (event.metadata?.tokenUsage) {
        return {
          totalInputTokens: acc.totalInputTokens + (event.metadata.tokenUsage.inputTokens || 0),
          totalOutputTokens: acc.totalOutputTokens + (event.metadata.tokenUsage.outputTokens || 0)
        };
      }
      return acc;
    }, { totalInputTokens: 0, totalOutputTokens: 0 });
  }, [events]);

  // Initialize accordion state when events change
  useEffect(() => {
    // Initialize new events with collapsed state
    const newState = { ...accordionState };
    events.forEach(event => {
      if (newState[event._id] === undefined) {
        newState[event._id] = false;
      }
    });
    setAccordionState(newState);
  }, [events]);

  useEffect(() => {
    dispatch(setFocusedNodeId(null));
    dispatch(resetEventFilters());
  }, []);

  useEffect(() => {
    // Scroll to bottom when new events are added or filters change
    // Use a small timeout to ensure the DOM has been updated
    const timeoutId = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [events.length, showEvents, showChat]);

  const handleExpandAll = () => {
    const newState = { ...accordionState };
    events.forEach(event => {
      newState[event._id] = true;
    });
    setAccordionState(newState);
  };

  const handleCollapseAll = () => {
    const newState = { ...accordionState };
    events.forEach(event => {
      newState[event._id] = false;
    });
    setAccordionState(newState);
  };

  const handleAccordionChange = (eventId: string, isExpanded: boolean) => {
    setAccordionState(prev => ({
      ...prev,
      [eventId]: isExpanded
    }));
    
    // If expanding, ensure the accordion is visible by scrolling to it
    if (isExpanded && scrollContainerRef.current) {
      const accordionElement = document.getElementById(`event-accordion-${eventId}`);
      if (accordionElement) {
        accordionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const getEventIcon = (type: EventType) => {
    const config = EVENT_TYPE_CONFIG[type];
    if (!config) {
      return <EventIcon sx={{ width: 16, height: 16, color: '#9e9e9e' }} />;
    }
    
    // Clone the icon element with the right size and color
    return React.cloneElement(config.icon as React.ReactElement, {
      sx: {
        width: 16,
        height: 16,
        color: config.color
      }
    });
  };

  // Type guard for CreditUsageEvent
  const isCreditUsageEvent = (event: NodeEvent): event is CreditUsageEvent => {
    return event.type === EventType.CREDIT_USAGE;
  };

  // Type guard for message events
  const isMessageEvent = (event: NodeEvent): event is OutgoingMessageEvent | IncomingMessageEvent => {
    return event.type === EventType.OUTGOING_MESSAGE || event.type === EventType.INCOMING_MESSAGE;
  };

  // Format date for display
  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString();
  };

  // Format number with K/M suffixes
  const formatNumber = (num: number): string => {
    if (num === 0) return '0';
    if (num < 1000) return num.toString();
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`.replace('.0K', 'K');
    return `${(num / 1000000).toFixed(1)}M`.replace('.0M', 'M');
  };

  // Type guard for session events
  const isSessionEvent = (event: NodeEvent): event is SessionStartedEvent | SessionEndedEvent => {
    return event.type === EventType.SESSION_STARTED || event.type === EventType.SESSION_ENDED;
  };

  // Type guard for tool events
  const isToolEvent = (event: NodeEvent): event is NodeEvent & {
    data: {
      toolName: string;
      toolInput: any;
      toolOutput?: any;
      toolUseId?: string;
      duration?: number;
      error?: string;
    }
  } => {
    if (event.type === EventType.TOOL_EVENT) {
      return true;
    }
    if (event.type === EventType.PROCESS && event.data?.processType === ProcessType.TOOL_USAGE) {
      return true;
    }
    return false;
  };

  // Render specific tool usage event
  const renderToolUsageEvent = (toolData: any, config: typeof EVENT_TYPE_CONFIG[EventType.TOOL_EVENT], statusDisplay: { text: string, color: string }) => {
    if (!toolData) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Tool Input */}
        {toolData.toolInput && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Input:</Typography>
            <Typography variant="body2" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                p: 1,
                borderRadius: 1,
                maxHeight: '100px',
                overflow: 'auto'
              }}
            >
              {typeof toolData.toolInput === 'string' ? toolData.toolInput : JSON.stringify(toolData.toolInput, null, 2)}
            </Typography>
          </Box>
        )}

        {/* Tool Output */}
        {toolData.toolOutput && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Output:</Typography>
            <Typography variant="body2" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                p: 1,
                borderRadius: 1,
                maxHeight: '100px',
                overflow: 'auto'
              }}
            >
              {Array.isArray(toolData.toolOutput) ? toolData.toolOutput.join('\n') : 
                typeof toolData.toolOutput === 'string' ? toolData.toolOutput : 
                JSON.stringify(toolData.toolOutput, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render tool events
  const renderToolEvent = (event: NodeEvent) => {
    if (!isToolEvent(event)) return null;

    const config = EVENT_TYPE_CONFIG[EventType.TOOL_EVENT];
    const toolData = event.data;
    
    if (!toolData) return null;

    // Get status display text and color
    const getStatusDisplay = () => {
      switch (event.status) {
        case EventStatus.SUCCESS:
          return { text: '(Success)', color: 'success.main' };
        case EventStatus.ERROR:
          return { text: '(Failed)', color: 'error.main' };
        case EventStatus.PENDING:
          return { text: '(Pending)', color: 'warning.main' };
        default:
          return { text: '', color: 'text.primary' };
      }
    };

    const statusDisplay = getStatusDisplay();
    
    return (
      <Box 
        key={event._id}
        sx={{ 
          my: 2, 
          p: 2, 
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon sx={{ color: config.color }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {toolData.toolName}
          </Typography>
          <Typography variant="body2" sx={{ color: statusDisplay.color }}>
            {statusDisplay.text}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {toolData.duration !== undefined && (
            <Chip
              label={`Duration: ${toolData.duration}ms`}
              size="small"
              sx={{
                backgroundColor: config.bgColor,
                color: config.color,
                mr: 1
              }}
            />
          )}
          
          {toolData.toolUseId && (
            <Chip
              label={`ID: ${toolData.toolUseId}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                color: '#2196f3',
              }}
            />
          )}
        </Box>

        {/* Render specific tool type content */}
        {renderToolUsageEvent(toolData, config, statusDisplay)}

        {/* Error Message */}
        {toolData.error && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="error">Error:</Typography>
            <Typography variant="body2" color="error.main" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
                p: 1,
                borderRadius: 1
              }}
            >
              {toolData.error}
            </Typography>
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {formatDate(event.timestamp)}
        </Typography>
      </Box>
    );
  };

  const renderEvent = (event: NodeEvent) => {
    // Tool events are rendered differently
    if (isToolEvent(event)) {
      return isAdmin ? renderToolEvent(event) : null;
    }

    // Session events are rendered differently
    if (isSessionEvent(event)) {
      return renderSessionEvent(event);
    }

    // Credit usage events are rendered differently
    if (isCreditUsageEvent(event)) {
      return renderCreditUsageEvent(event);
    }

    // Message events are rendered differently
    if (isMessageEvent(event)) {
      return renderMessageEvent(event);
    }

    // Render other event types
    const config = EVENT_TYPE_CONFIG[event.type];
    
    return (
      <EventAccordion
        key={event._id}
        id={event._id}
        isExpanded={!!accordionState[event._id]}
        onExpandChange={(isExpanded) => handleAccordionChange(event._id, isExpanded)}
        icon={getEventIcon(event.type)}
        type={event.type}
        nodeType={event.nodeType}
        timestamp={event.timestamp}
        nodeId={event.nodeId}
        data={event.data}
        isFocused={focusedNodeId === event.nodeId}
        typeLabel={config.label}
        typeBgColor={config.bgColor}
        typeColor={config.color}
        creditsUsed={event.creditsUsed}
        metadata={event.metadata}
      />
    );
  };

  // Render credit usage events
  const renderCreditUsageEvent = (event: CreditUsageEvent) => {
    const config = EVENT_TYPE_CONFIG[event.type];
    const creditData = event.data;
    const tokenUsage = event.metadata?.tokenUsage;
    
    return (
      <Box 
        key={event._id}
        sx={{ 
          my: 2, 
          p: 2, 
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon color="secondary" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {config.label}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Display credits - always 1 per LLM call */}
          <Chip
            label={`${formatNumber(creditData.amount)} credit${creditData.amount !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(156, 39, 176, 0.1)',
              color: 'rgb(156, 39, 176)',
              mr: 1
            }}
          />
          
          {/* Display token usage if available */}
          {tokenUsage && (
            <>
              <Tooltip title={`Input tokens: ${formatNumber(tokenUsage.inputTokens || 0)}`}>
                <Chip
                  label={`In: ${formatNumber(tokenUsage.inputTokens || 0)}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    color: 'rgb(76, 175, 80)',
                    mr: 1
                  }}
                />
              </Tooltip>
              
              <Tooltip title={`Output tokens: ${formatNumber(tokenUsage.outputTokens || 0)}`}>
                <Chip
                  label={`Out: ${formatNumber(tokenUsage.outputTokens || 0)}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(3, 169, 244, 0.1)',
                    color: 'rgb(3, 169, 244)',
                    mr: 1
                  }}
                />
              </Tooltip>
              
              <Tooltip title={`Total tokens: ${formatNumber(tokenUsage.totalTokens || 0)}`}>
                <Chip
                  label={`Total: ${formatNumber(tokenUsage.totalTokens || 0)}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    color: 'rgb(255, 152, 0)',
                  }}
                />
              </Tooltip>
            </>
          )}
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {formatDate(event.timestamp)}
        </Typography>
      </Box>
    );
  };

  // Render message events
  const renderMessageEvent = (event: OutgoingMessageEvent | IncomingMessageEvent) => {
    const config = EVENT_TYPE_CONFIG[event.type];
    
    return (
      <MessageEvent
        key={event._id}
        message={event.data?.message}
        timestamp={event.timestamp}
        type={event.type}
        color={config.color}
        bgColor={config.bgColor}
      />
    );
  };

  // Render session events
  const renderSessionEvent = (event: SessionStartedEvent | SessionEndedEvent) => {
    const config = EVENT_TYPE_CONFIG[event.type];
    const isSessionStartEvent = event.type === EventType.SESSION_STARTED;
    const tokenUsage = event.metadata?.tokenUsage;
    
    return (
      <Box 
        key={event._id}
        sx={{ 
          my: 2, 
          p: 2, 
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSessionStartEvent ? <PlayCircleOutlineIcon color="success" /> : <StopCircleIcon color="error" />}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {config.label}: {isSessionStartEvent ? 'New conversation started' : 'Conversation ended'}
            {event.data?.sender && ` by ${event.data.sender}`}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <div ref={scrollContainerRef} style={{ overflowY: 'auto', height: '100%' }}>
      {events.map(event => renderEvent(event)).filter(x => x !== null)}
    </div>
  );
};

export default NodeEventsList;